import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Clock4,
  LucideCalendarDays,
  Radio,
  Users,
  FileText,
} from "lucide-react";
import RichTextRenderer from "./RichTextRenderer";
import "./EventModal.css";

const EventModal = ({
  isOpen,
  onClose,
  event,
  isUpcoming = true,
  onRegister,
}) => {
  // Close modal on Escape key press and handle scroll behavior
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleWheel = (e) => {
      // Allow scrolling within the modal content
      e.stopPropagation();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";

      // Add wheel event listener to modal content to fix scroll issues
      const modalContent = document.querySelector("[data-modal-content]");
      if (modalContent) {
        modalContent.addEventListener("wheel", handleWheel, { passive: true });
      }
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";

      // Remove wheel event listener
      const modalContent = document.querySelector("[data-modal-content]");
      if (modalContent) {
        modalContent.removeEventListener("wheel", handleWheel);
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const getHeaderTitle = () => {
    const isHappeningNow = event.status === 'happening-now';
    
    if (event.category === "webinar") {
      return isUpcoming ? `Webinar #${event.id}` : `Past Webinar #${event.id}`;
    } else if (event.category === "hackathon") {
      return isUpcoming
        ? `Hackathon #${event.id}`
        : `Past Hackathon #${event.id}`;
    }
    return isUpcoming ? `Event #${event.id}` : `Past Event #${event.id}`;
  };

  const isHappeningNow = event.status === 'happening-now';
  const isDisplayOnly = event.status === 'displayOnly';
  const isRegisterDisabled = isHappeningNow || !event.registerLink;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        data-modal-content
        className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar"
        onWheel={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div
          className={`${
            isUpcoming ? "bg-Color-Primary/70" : "bg-gray-600/80"
          } py-4 px-6 text-white rounded-t-xl`}
        >
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-semibold">{getHeaderTitle()}</h2>
            {isHappeningNow && (
              <div className="bg-white/20 text-white px-2 py-1 rounded-full font-bold text-xs flex items-center gap-1">
                <Radio className="size-3.5" />
                HAPPENING NOW
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Side - Event Details */}
            <div className="space-y-6">
              {/* Title */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">
                  {event.title}
                </h3>

                {/* Registration/Attendee Count - Only show if the count exists */}
                {isUpcoming && event.registrationCount ? (
                  <p className="text-gray-700 text-lg mb-6">
                    {event.registrationCount} people registered for this event
                  </p>
                ) : !isUpcoming && event.attendeeCount ? (
                  <p className="text-gray-700 text-lg mb-6">
                    {event.attendeeCount} people attended this event
                  </p>
                ) : null}
              </div>

              {/* Event Details */}
              <div className="space-y-4">
                {/* Date */}
                {event.date && (
                  <div className="flex items-center text-gray-700">
                    <LucideCalendarDays className="w-6 h-6 text-gray-600 mr-3" />
                    <span className="text-lg font-medium">{event.date}</span>
                  </div>
                )}

                {/* Time */}
                {event.time && (
                  <div className="flex items-center text-gray-700">
                    <Clock4 className="w-6 h-6 text-gray-600 mr-3" />
                    <span className="text-lg font-medium">{event.time}</span>
                  </div>
                )}

                {/* Platform */}
                {event.platform && (
                  <div className="flex items-center text-gray-700">
                    <Radio className="w-6 h-6 text-gray-600 mr-3" />
                    <span className="text-lg font-medium">{event.platform}</span>
                  </div>
                )}
              {event.brief && (
              <div className="text-Color-Black">
                <p className="text-base">{event.brief}</p>
              </div>
            )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-6">
                {isUpcoming ? (
                  <>
                    {!isDisplayOnly && (
                      <button
                        onClick={() => {
                          onClose();
                          if (event.registerLink && !isHappeningNow) {
                            window.open(event.registerLink, "_blank");
                          }
                        }}
                        disabled={isRegisterDisabled}
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                          isRegisterDisabled 
                            ? 'bg-gray-400 text-white cursor-not-allowed' 
                            : 'bg-Color-Brand-Red text-white hover:bg-red-900'
                        }`}
                      >
                        {isHappeningNow ? 'CLOSED' : (!event.registerLink ? 'OPEN SOON!' : 'REGISTER NOW')}
                      </button>
                    )}

                    {/* Guideline Button - Only show for upcoming events with guideline */}
                    {event.guideline && (
                      <button
                        onClick={() => {
                          window.open(event.guideline, "_blank");
                        }}
                        className="w-full bg-Color-Brand/5 border border-Color-Brand/20 text-gray-600 py-3 px-6 rounded-lg font-semibold hover:bg-Color-Brand/10 transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        <FileText className="w-5 h-5" />
                        VIEW GUIDELINES
                      </button>
                    )}
                  </>
                ) : (
                  <button className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors duration-200">
                    EXPIRED
                  </button>
                )}
              </div>
            </div>

            {/* Right Side - Flyer Image */}
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Event Flyer
              </h4>
              <div className="relative">
                <img
                  src={event.flyerImage}
                  alt={`${event.title} Flyer`}
                  className="w-full rounded-lg shadow-lg object-cover"
                  style={{ aspectRatio: "3/4", maxHeight: "500px" }}
                />
                {/* Overlay for better readability if needed */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
              </div>
            </div>
          </div>

          {/* Additional Event Description */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="text-xl font-semibold text-gray-900 mb-4">
              About This Event
            </h4>
            <div className="leading-relaxed">
              <RichTextRenderer
                text={
                  event.description ||
                  "Join us for an exciting and informative session covering cutting-edge topics in engineering and technology. This event is designed for professionals, students, and enthusiasts who want to stay updated with the latest developments in the field. Don't miss this opportunity to learn from industry experts and network with like-minded individuals."
                }
                textClassName="text-gray-700"
                className="leading-relaxed"
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EventModal;
